import { useEffect, useMemo, useState } from 'react';
import Modal from '../common/Modal';
import { orderApi } from '../../api/services';
import toast from 'react-hot-toast';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

function addWorkingDaysISO(iso, n) {
  const d = iso ? new Date(iso) : new Date();
  let added = 0;
  while (added < n) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0) added += 1;
  }
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Edit the split plan of a whole MBO-order family (docs/split-orders-redesign.md §10).
 *
 * Rows: every product line across all parts. Columns: the parts. Pre-filled
 * with the current allocation. The user can remove a part column, add one,
 * and move quantities between columns — each row must keep its total.
 * Saving replaces the family's split plan atomically; every part returns to
 * review owned by the editor.
 */
export default function EditSplitModal({ isOpen, onClose, onDone, orderId }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // cols: [{ orderId|null, label, scheduledDate, reshipLinked, qtyByItem }]
  const [cols, setCols] = useState([]);
  // removing: { colIdx, targets: number[] } — pending "where do the
  // quantities go?" chooser for a part-with-quantities being removed.
  const [removing, setRemoving] = useState(null);

  useEffect(() => {
    if (!isOpen || !orderId) return;
    setLoading(true);
    setRemoving(null);
    orderApi.getSplitPlan(orderId)
      .then((res) => {
        const p = res.data;
        setPlan(p);
        // One column per part; cells pre-filled with the part's current
        // share of each product (products are merged family-wide).
        setCols(p.parts.map((part) => ({
          orderId: part.orderId,
          label: `Part ${part.partNumber}`,
          scheduledDate: part.scheduledDate ? String(part.scheduledDate).slice(0, 10) : new Date().toISOString().slice(0, 10),
          reshipLinked: part.reshipLinked,
          qtyByItem: Object.fromEntries(
            p.products.map((prod) => [prod.orderItemId, prod.perPart[part.orderId] || 0])
          ),
        })));
      })
      .catch((err) => {
        toast.error(err.response?.data?.error || 'Failed to load split plan');
        onClose();
      })
      .finally(() => setLoading(false));
  }, [isOpen, orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  // One row per product across the whole family (server merges same-product
  // clones from different parts into a single pool).
  const rows = useMemo(() => plan?.products || [], [plan]);

  const rowSums = useMemo(() => {
    const out = {};
    for (const r of rows) {
      out[r.orderItemId] = cols.reduce((a, c) => a + (c.qtyByItem[r.orderItemId] || 0), 0);
    }
    return out;
  }, [rows, cols]);
  const allRowsMatch = rows.every((r) => rowSums[r.orderItemId] === r.totalQty);
  const allColsNonEmpty = cols.every((c) => Object.values(c.qtyByItem).some((v) => v > 0));

  function updateQty(colIdx, itemId, value) {
    const v = Math.max(0, parseInt(value, 10) || 0);
    setCols((prev) => prev.map((c, i) =>
      i === colIdx ? { ...c, qtyByItem: { ...c.qtyByItem, [itemId]: v } } : c
    ));
  }
  function updateDate(colIdx, value) {
    setCols((prev) => prev.map((c, i) => (i === colIdx ? { ...c, scheduledDate: value } : c)));
  }
  function addCol() {
    setCols((prev) => {
      const last = prev[prev.length - 1];
      return [...prev, {
        orderId: null,
        label: 'New part',
        scheduledDate: addWorkingDaysISO(last?.scheduledDate, 3),
        reshipLinked: false,
        qtyByItem: {},
      }];
    });
  }
  function removeCol(colIdx) {
    if (cols.length <= 1) return;
    const c = cols[colIdx];
    if (c.reshipLinked) {
      toast.error('This part has a reship linked to it and cannot be removed');
      return;
    }
    const hasQty = Object.values(c.qtyByItem).some((v) => v > 0);
    if (!hasQty) {
      setCols((prev) => prev.filter((_, i) => i !== colIdx));
      return;
    }
    // The part holds quantities — ask where they should go.
    setRemoving({ colIdx, targets: cols.map((_, i) => i).filter((i) => i !== colIdx) });
  }

  function toggleRemoveTarget(i) {
    setRemoving((prev) => {
      if (!prev) return prev;
      const targets = prev.targets.includes(i)
        ? prev.targets.filter((t) => t !== i)
        : [...prev.targets, i].sort((a, b) => a - b);
      return { ...prev, targets };
    });
  }

  // Distribute the removed part's quantities evenly across the selected
  // target parts (per product; remainders go to the earliest targets),
  // then drop the column.
  function confirmRemove() {
    if (!removing || removing.targets.length === 0) return;
    const { colIdx, targets } = removing;
    setCols((prev) => {
      const removed = prev[colIdx];
      const next = prev.map((c) => ({ ...c, qtyByItem: { ...c.qtyByItem } }));
      for (const [itemId, qty] of Object.entries(removed.qtyByItem)) {
        if (!qty || qty <= 0) continue;
        const base = Math.floor(qty / targets.length);
        let remainder = qty % targets.length;
        for (const t of targets) {
          const add = base + (remainder > 0 ? 1 : 0);
          if (remainder > 0) remainder -= 1;
          if (add > 0) next[t].qtyByItem[itemId] = (next[t].qtyByItem[itemId] || 0) + add;
        }
      }
      return next.filter((_, i) => i !== colIdx);
    });
    setRemoving(null);
  }

  async function save() {
    if (!allRowsMatch) {
      toast.error('Each product\'s quantities must sum to its order total');
      return;
    }
    if (!allColsNonEmpty) {
      toast.error('Each part must contain at least one item with a positive quantity');
      return;
    }
    setSaving(true);
    try {
      const res = await orderApi.editSplit(orderId, {
        parts: cols.map((c) => ({
          orderId: c.orderId,
          scheduledDate: c.scheduledDate,
          items: Object.entries(c.qtyByItem)
            .filter(([, q]) => q > 0)
            .map(([itemId, quantity]) => ({ orderItemId: itemId, quantity })),
        })),
      });
      toast.success(`Split updated — now ${res.data.totalParts} part(s), all back in review`);
      onDone?.(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Edit split failed');
    } finally { setSaving(false); }
  }

  if (!isOpen) return null;

  // Scale the dialog with the number of part columns so the grid fits
  // without horizontal scrolling for typical splits.
  const modalSize = cols.length <= 2 ? 'xl' : cols.length <= 4 ? '2xl' : 'full';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={modalSize} title={`Edit split — ${plan?.externalId || ''}`}>
      {loading || !plan ? (
        <div className="flex items-center gap-2 p-6 text-sm text-gray-600">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading current split…
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            This order is split into <strong>{plan.parts.length} parts</strong>.
            Remove a part, add one, or move quantities between parts — each
            product's total must stay the same. Saving sends <strong>all parts
            back to review</strong>.
          </p>

          <div className="border border-gray-200 rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="text-left px-3 py-2 sticky left-0 bg-gray-50">Product</th>
                  <th className="text-right px-3 py-2">Total</th>
                  {cols.map((c, i) => (
                    <th key={i} className="text-left px-3 py-2 border-l border-gray-200">
                      <div className="flex items-center gap-1">
                        <span>{c.orderId ? c.label : 'New part'}</span>
                        {cols.length > 1 && (
                          <button onClick={() => removeCol(i)}
                                  className="p-0.5 text-red-600 hover:bg-red-50 rounded"
                                  title="Remove this part">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
                <tr>
                  <th className="text-left px-3 py-2 sticky left-0 bg-gray-50 font-normal text-gray-500 whitespace-nowrap">Scheduled date</th>
                  <th></th>
                  {cols.map((c, i) => (
                    <td key={i} className="px-3 py-2 border-l border-gray-200">
                      <input type="date" value={c.scheduledDate}
                             onChange={(e) => updateDate(i, e.target.value)}
                             className="px-2 py-1 border border-gray-300 rounded text-xs" />
                    </td>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r) => {
                  const ok = rowSums[r.orderItemId] === r.totalQty;
                  return (
                    <tr key={r.orderItemId}>
                      <td className="px-3 py-2 sticky left-0 bg-white min-w-[200px]">
                        <div className="font-medium text-gray-900 whitespace-nowrap">{r.productName}</div>
                        {r.indianBrand && (
                          <div className="text-xs text-gray-500 whitespace-nowrap">{r.indianBrand}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-700">{r.totalQty}</td>
                      {cols.map((c, i) => (
                        <td key={i} className="px-3 py-2 border-l border-gray-200">
                          <input type="number" min="0" max={r.totalQty} value={c.qtyByItem[r.orderItemId] ?? 0}
                                 onChange={(e) => updateQty(i, r.orderItemId, e.target.value)}
                                 className={`w-20 px-2 py-1 border rounded text-sm ${ok ? 'border-gray-300' : 'border-red-300 bg-red-50'}`} />
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-50 text-xs">
                <tr>
                  <td className="px-3 py-2 sticky left-0 bg-gray-50 font-medium text-gray-700">Part totals</td>
                  <td></td>
                  {cols.map((c, i) => (
                    <td key={i} className="px-3 py-2 border-l border-gray-200 text-gray-600">
                      <strong>{Object.values(c.qtyByItem).reduce((a, b) => a + (b || 0), 0)}</strong>
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>

          {removing && cols[removing.colIdx] && (
            <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg space-y-2">
              <div className="text-sm font-medium text-amber-900">
                Remove {cols[removing.colIdx].orderId ? cols[removing.colIdx].label : 'this new part'} — where
                should its {Object.values(cols[removing.colIdx].qtyByItem).reduce((a, b) => a + (b || 0), 0)} units go?
              </div>
              <div className="text-xs text-amber-800">
                Pick one or more parts. Quantities are split evenly between the parts you pick
                (pick just one to move everything there). You can still fine-tune the numbers afterwards.
              </div>
              <div className="flex flex-wrap gap-2">
                {cols.map((c, i) => i !== removing.colIdx && (
                  <button key={i} onClick={() => toggleRemoveTarget(i)}
                          className={`px-2.5 py-1 rounded-md border text-xs font-medium ${
                            removing.targets.includes(i)
                              ? 'bg-amber-600 text-white border-amber-700'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-amber-100'
                          }`}>
                    {c.orderId ? c.label : `New part`} · {format(new Date(c.scheduledDate), 'dd MMM')}
                  </button>
                ))}
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setRemoving(null)}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs hover:bg-white">
                  Cancel
                </button>
                <button onClick={confirmRemove} disabled={removing.targets.length === 0}
                        className="px-3 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-medium hover:bg-amber-700 disabled:opacity-50">
                  {removing.targets.length === 0
                    ? 'Pick at least one part'
                    : removing.targets.length === 1
                      ? `Move all to ${cols[removing.targets[0]].orderId ? cols[removing.targets[0]].label : 'new part'} & remove`
                      : `Split evenly between ${removing.targets.length} parts & remove`}
                </button>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <button onClick={addCol} className="flex items-center gap-1 text-xs text-blue-600 hover:underline">
              <Plus className="w-3.5 h-3.5" /> Add a part
            </button>
            <div className={`text-xs font-medium ${allRowsMatch ? 'text-green-700' : 'text-red-700'}`}>
              {allRowsMatch ? 'All products balanced ✓' : 'Each row must sum to its total'}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
            <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
            <button onClick={save} disabled={saving || !allRowsMatch || !allColsNonEmpty}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save split ({cols.length} part{cols.length === 1 ? '' : 's'})
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
